// =============================================================================
// Contact form.
//
// Submits to the child theme's own REST route and reports back through the
// `role="status"` paragraph, which is what makes the outcome reach a screen
// reader rather than only the eye.
//
// The payload is the form's own `FormData`, so this module never names a field.
// The honeypot travels with it for the same reason — the server decides what a
// filled-in `website` means, and adding a field to the block needs no change
// here.
// =============================================================================

import { cfg, str } from "./config.js";

/**
 * Last resort, reached only if the inline config failed to print at all — which
 * also means `genericError` is not there to use. Deliberately untranslated:
 * every translated copy this module shows comes from the server.
 */
const FALLBACK_ERROR = "That didn't go through. Please try again.";

export default function initContactForm() {
	document.querySelectorAll("[data-contact-form]").forEach(setupForm);
}

/** @param {HTMLFormElement} form */
function setupForm(form) {
	const status = form.querySelector("[data-form-status]");
	const submit = form.querySelector("[data-contact-submit]");
	const endpoint = contactConfig().endpoint;

	form.addEventListener("submit", async (event) => {
		// The form carries no `action`, so without this the browser would post to
		// the current URL and reload the page — taking the typed message with it.
		event.preventDefault();

		// Native validation has already run: a `submit` event only reaches here
		// once every `required` field and the email's `type` have passed.

		if (!endpoint) {
			report(status, refusal(""), true);
			return;
		}

		if (submit) {
			submit.disabled = true;
		}

		report(status, str("sending", "Sending…"), false);

		let result;

		try {
			result = await send(endpoint, Object.fromEntries(new FormData(form)));
		} catch {
			result = { ok: false, message: "" };
		}

		if (submit) {
			submit.disabled = false;
		}

		if (result.ok) {
			// Cleared only on success: a rejected message stays on screen so the
			// visitor can correct it and try again without retyping.
			form.reset();
			report(status, result.message || str("sent", "Message sent"), false);
			return;
		}

		report(
			status,
			// The route writes its own refusals — an unreadable address, a rate
			// limit — and says which, so those are passed through untouched.
			refusal(result.message),
			true
		);
	});
}

/**
 * The message to show for a failed send.
 *
 * @param {string} fromServer Whatever the route returned, if anything.
 * @return {string}
 */
function refusal(fromServer) {
	return fromServer || contactConfig().genericError || FALLBACK_ERROR;
}

/**
 * Post the form and read the outcome.
 *
 * @param {string}                 endpoint Route URL.
 * @param {Record<string, string>} payload  Field name => value.
 * @return {Promise<{ok: boolean, message: string}>}
 */
async function send(endpoint, payload) {
	const response = await fetch(endpoint, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(payload),
	});

	// A body that is not JSON means something failed underneath WordPress — a
	// fatal, or a proxy in front of it — so the parse is guarded rather than
	// assumed and the generic refusal stands in.
	const data = await response.json().catch(() => ({}));

	return {
		ok: response.ok,
		message: typeof data?.message === "string" ? data.message : "",
	};
}

/**
 * Write the status line.
 *
 * @param {HTMLElement|null} status  The `[data-form-status]` paragraph.
 * @param {string}           message What to say.
 * @param {boolean}          isError Whether to paint it as a refusal.
 */
function report(status, message, isError) {
	if (!status) {
		return;
	}

	status.textContent = message;
	status.classList.add("is-visible");
	status.classList.toggle("is-error", isError);
}

/** @return {Record<string, string>} */
function contactConfig() {
	const contact = cfg().contact;

	return contact && typeof contact === "object" ? contact : {};
}
